import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { experienceService } from '../services/experience.service';
import type { UpdateExperienceRequest, Experience } from '../types/experience.types';

interface UpdateExperienceData {
  candidateId: string;
  experienceId: string;
  request: UpdateExperienceRequest;
}

type UpdateMutation = UseMutationResult<Experience, Error, UpdateExperienceData>;

interface UseUpdateExperienceReturn {
  updateExperience: UpdateMutation['mutate'];
  updateExperienceAsync: UpdateMutation['mutateAsync'];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  reset: () => void;
}

export function useUpdateExperience(): UseUpdateExperienceReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: UpdateExperienceData) =>
      experienceService.updateExperience(data.candidateId, data.experienceId, data.request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['experiences', { candidateId: variables.candidateId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['experience', variables.candidateId, variables.experienceId],
      });
    },
  });

  return {
    updateExperience: mutation.mutate,
    updateExperienceAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}
